import React, { useState } from 'react'
import {
  Brain, ChevronRight, CheckCircle, XCircle,
  Lightbulb, RotateCcw, MessageCircle, Target,
  AlertTriangle, BookOpen, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react'
import { useProgressStore, useUnitProgress } from '../store/progressStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'N2-2'

// ── 튜터 시나리오 데이터 ──────────────────────────────────────────────────────
interface TutorScenario {
  id: string
  label: string
  emoji: string
  category: string
  learnStepId: string
  question: string
  correctAnswer: string
  wrongAnswer: string
  misconceptionCode: string
  note: string
  analysis: {
    diagnosis: string
    rootCause: string
    correction: string
    example: string
  }
  followUps: Array<{ q: string; a: string }>
}

const DEMO_SCENARIOS: TutorScenario[] = [
  {
    id: 'S1',
    label: '시나리오 1',
    emoji: '√',
    category: '제곱근의 뜻',
    learnStepId: 'NQ01',
    question: '9의 제곱근은 무엇인가요?',
    correctAnswer: '±3 (양의 제곱근 3과 음의 제곱근 -3)',
    wrongAnswer: '3',
    misconceptionCode: 'NM01',
    note: '√와 "제곱근" 개념을 혼동 — √9=3은 양의 제곱근이고, 9의 제곱근은 ±3',
    analysis: {
      diagnosis: '학생이 "9의 제곱근"과 "√9"를 같은 것으로 혼동하고 있습니다.',
      rootCause:
        '√9는 9의 양의 제곱근만을 나타내지만, "9의 제곱근"은 제곱하면 9가 되는 모든 수(+3과 -3)를 의미합니다.',
      correction:
        '제곱근의 개념: x² = a 일 때, x가 a의 제곱근. 9의 제곱근은 3²=9, (-3)²=9 이므로 ±3 두 개입니다.',
      example: '√9 = 3 (양의 제곱근만)\n9의 제곱근 = ±3 (두 개)\n√9 ≠ "9의 제곱근"',
    },
    followUps: [
      { q: '그럼 √25는 얼마인가요?', a: '√25 = 5 (양의 제곱근만. 25의 제곱근은 ±5)' },
      { q: '0의 제곱근은 몇 개인가요?', a: '0의 제곱근은 0 하나뿐입니다. 0² = 0' },
    ],
  },
  {
    id: 'S2',
    label: '시나리오 2',
    emoji: '|a|',
    category: '제곱근의 성질',
    learnStepId: 'NQ02',
    question: '√((-5)²)을 계산하세요.',
    correctAnswer: '5',
    wrongAnswer: '-5',
    misconceptionCode: 'NM04',
    note: '√(a²) = |a| 원리를 모르고 √와 ²이 단순 상쇄된다고 오해',
    analysis: {
      diagnosis: '학생이 √((-5)²) = -5라고 답했습니다. √와 ²이 상쇄되어 -5가 남는다고 생각한 것입니다.',
      rootCause:
        '√(a²) = a라고 잘못 외워서 음수에 그대로 적용했습니다. 사실 √(a²) = |a| (절댓값)입니다.',
      correction:
        '√(a²) = |a|. 루트 안에서 제곱이 이루어지면 결과는 항상 양수(절댓값)입니다.\n(-5)² = 25, √25 = 5이므로 √((-5)²) = 5.',
      example:
        '√(3²) = |3| = 3\n√((-3)²) = |-3| = 3\n√(a²) ≥ 0 (항상 양수 또는 0)',
    },
    followUps: [
      { q: '(√5)²은 얼마인가요?', a: '(√5)² = 5. √와 ²은 역연산이므로 5가 됩니다.' },
      {
        q: 'a = -2일 때, √(a²) + a의 값은?',
        a: '√((-2)²) + (-2) = |-2| + (-2) = 2 - 2 = 0',
      },
    ],
  },
  {
    id: 'S3',
    label: '시나리오 3',
    emoji: '🌀',
    category: '무리수 판별',
    learnStepId: 'NQ03',
    question: '√4는 유리수인가요, 무리수인가요?',
    correctAnswer: '유리수 (√4 = 2이므로)',
    wrongAnswer: '무리수 (루트가 있으니까)',
    misconceptionCode: 'NM05',
    note: '루트 기호만 보고 무조건 무리수라고 판단하는 오류',
    analysis: {
      diagnosis: '학생이 "√가 붙어 있으면 무리수"라는 잘못된 규칙을 적용하고 있습니다.',
      rootCause:
        '√n에서 n이 완전제곱수이면 √n은 자연수/정수가 되어 유리수입니다. √가 붙어 있어도 완전제곱수면 유리수입니다.',
      correction:
        '무리수 판별 기준: √n이 유리수인지 확인.\n• √n = 정수이면 유리수 (n이 완전제곱수)\n• √n이 정수가 아닌 무한소수이면 무리수 (n이 완전제곱수가 아님)',
      example:
        '√4 = 2 → 유리수\n√9 = 3 → 유리수\n√2 ≈ 1.414... → 무리수\n√7 ≈ 2.645... → 무리수',
    },
    followUps: [
      {
        q: '다음 중 무리수를 고르세요: √16, √5, √(1/4)',
        a: '√5만 무리수. √16=4, √(1/4)=1/2은 유리수.',
      },
      { q: '0.121212...는 유리수인가요?', a: '순환소수이므로 유리수입니다. (= 4/33)' },
    ],
  },
  {
    id: 'S4',
    label: '시나리오 4',
    emoji: '📊',
    category: '대소 비교',
    learnStepId: 'NQ04',
    question: '√3과 1.7의 크기를 비교하세요.',
    correctAnswer: '√3 > 1.7 ((√3)² = 3 > (1.7)² = 2.89)',
    wrongAnswer: '√3 < 1.7 (루트라서 작을 것 같아서)',
    misconceptionCode: 'NM07',
    note: '루트가 있으면 작다는 선입견. 제곱해서 비교하는 방법을 모름',
    analysis: {
      diagnosis: '직관적으로 "루트가 있으면 작다"고 판단한 오류입니다.',
      rootCause:
        '두 양수를 비교할 때 제곱해서 비교하는 방법을 모르거나, 루트 기호에 대한 막연한 선입견이 있습니다.',
      correction:
        '양수 a, b에 대해: a > b ⟺ a² > b²\n(√3)² = 3, (1.7)² = 2.89\n3 > 2.89 이므로 √3 > 1.7',
      example: '√5와 2.2 비교:\n(√5)² = 5, (2.2)² = 4.84\n5 > 4.84 → √5 > 2.2',
    },
    followUps: [
      { q: '√7과 √6 중 어느 것이 더 큰가요?', a: '7 > 6이므로 √7 > √6. 루트 안의 수가 크면 루트값도 큽니다.' },
      {
        q: '√2와 1.5의 대소를 비교하세요.',
        a: '(√2)² = 2, (1.5)² = 2.25. 2 < 2.25이므로 √2 < 1.5',
      },
    ],
  },
  {
    id: 'S5',
    label: '시나리오 5',
    emoji: '➕',
    category: '제곱근 덧셈',
    learnStepId: 'NQ06',
    question: '√2 + √3을 간단히 하면?',
    correctAnswer: '√2 + √3 (더 이상 간단히 할 수 없음)',
    wrongAnswer: '√5',
    misconceptionCode: 'NM10',
    note: '√a + √b = √(a+b)라는 잘못된 계산. 루트는 루트 안이 같아야만 더할 수 있음',
    analysis: {
      diagnosis: '√2 + √3 = √5라는 잘못된 계산 — 루트를 덧셈처럼 취급한 오류입니다.',
      rootCause:
        '√의 덧셈은 곱셈 성질(√a × √b = √(ab))과 달리, 루트 안의 수가 같아야만 더할 수 있습니다. √2 + √3은 더 이상 간단히 할 수 없습니다.',
      correction:
        '덧셈 가능: 루트 안의 수가 같을 때만!\n2√3 + 5√3 = 7√3 ✓\n√2 + √3 = 더 이상 간단히 안 됨 ✓',
      example:
        '확인: √2 ≈ 1.41, √3 ≈ 1.73 → 합 ≈ 3.14\n√5 ≈ 2.24 → 3.14 ≠ 2.24\n즉 √2 + √3 ≠ √5',
    },
    followUps: [
      { q: '2√3 + 4√3은 얼마인가요?', a: '(2+4)√3 = 6√3. 루트 안의 수가 같으면 계수끼리 더합니다.' },
      {
        q: '√8 + √2를 간단히 하세요.',
        a: '√8 = 2√2이므로 2√2 + √2 = 3√2',
      },
    ],
  },
]

const SCENARIO_KEYS = DEMO_SCENARIOS.map(s => s.id)

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────
function FragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const visible = fragments.slice(0, 2)
  if (visible.length === 0) return null
  return (
    <div className="card border border-amber-200 bg-amber-50 mb-4">
      <p className="text-xs font-bold text-amber-700 mb-2">⚡ AI 예상 취약 개념</p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <span className={`text-xs font-semibold flex-1 ${cfg.color}`}>{f.fragment.title}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function FollowUpCard({ followUps }: { followUps: TutorScenario['followUps'] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-bold text-purple-600 mb-1">💬 추가 질문</p>
      {followUps.map((fu, i) => (
        <div key={i} className="border border-purple-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
            className="w-full flex items-center justify-between px-3 py-2 bg-purple-50 hover:bg-purple-100 text-left transition-colors"
          >
            <span className="text-xs text-slate-700 font-medium flex-1">{fu.q}</span>
            {openIdx === i
              ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" />
              : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
          </button>
          {openIdx === i && (
            <div className="px-3 py-2 bg-white">
              <p className="text-xs text-purple-700 font-semibold whitespace-pre-line">{fu.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
export default function N4TutorPage() {
  const [selectedId, setSelectedId] = useState<string>(SCENARIO_KEYS[0])
  const [step, setStep] = useState<'question' | 'wrong' | 'analysis' | 'correct'>('question')
  const [showNote, setShowNote] = useState(false)

  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)

  const scenario = DEMO_SCENARIOS.find(s => s.id === selectedId)!

  const reset = () => {
    setStep('question')
    setShowNote(false)
  }

  const handleSelect = (id: string) => {
    setSelectedId(id)
    reset()
  }

  return (
    <div className="p-4 space-y-4">
      <FragmentPanel fragments={predictedFragments} />

      {/* 헤더 */}
      <div className="card bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <Brain size={20} className="text-purple-600" />
          </div>
          <div>
            <h2 className="text-base font-black text-slate-800">AI 튜터 — 제곱근과 실수</h2>
            <p className="text-xs text-slate-500">N2-2 오개념 분석 · 맞춤 피드백</p>
          </div>
        </div>
      </div>

      {/* 시나리오 선택 */}
      <div>
        <p className="text-xs font-bold text-slate-500 mb-2 px-1">📋 학습 시나리오 선택</p>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {DEMO_SCENARIOS.map(s => (
            <button
              key={s.id}
              onClick={() => handleSelect(s.id)}
              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                selectedId === s.id
                  ? 'bg-purple-500 text-white border-purple-500 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-purple-50 hover:border-purple-200'
              }`}
            >
              {s.emoji} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 시나리오 카드 */}
      <div className="card border-2 border-purple-200">
        {/* 카테고리 배지 */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-semibold">
            {scenario.category}
          </span>
          <span className="text-xs text-slate-400">{scenario.learnStepId}</span>
        </div>

        {/* 질문 단계 */}
        {step === 'question' && (
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 mb-1 flex items-center gap-1">
                <MessageCircle size={11} /> 학생 질문
              </p>
              <p className="text-sm font-semibold text-slate-800">{scenario.question}</p>
            </div>
            <p className="text-xs text-slate-500 text-center">학생의 답을 먼저 예상해보세요.</p>
            <button
              onClick={() => setStep('wrong')}
              className="w-full py-2.5 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
            >
              <ChevronRight size={15} /> 학생 답 확인
            </button>
          </div>
        )}

        {/* 오답 단계 */}
        {step === 'wrong' && (
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 mb-1">📝 문제</p>
              <p className="text-sm text-slate-700">{scenario.question}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 border border-red-200">
              <p className="text-xs font-bold text-red-500 mb-1 flex items-center gap-1">
                <XCircle size={11} /> 학생 오답
              </p>
              <p className="text-sm font-bold text-red-700">{scenario.wrongAnswer}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowNote(!showNote)}
                className="flex-1 py-2 bg-amber-100 text-amber-700 rounded-xl text-xs font-semibold hover:bg-amber-200 transition-colors flex items-center justify-center gap-1"
              >
                <AlertTriangle size={12} /> 오개념 메모
              </button>
              <button
                onClick={() => setStep('analysis')}
                className="flex-1 py-2 bg-purple-500 text-white rounded-xl text-xs font-bold hover:bg-purple-600 transition-colors flex items-center justify-center gap-1"
              >
                <Brain size={12} /> AI 분석
              </button>
            </div>
            {showNote && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs font-bold text-amber-700 mb-1">⚠️ 오개념 [{scenario.misconceptionCode}]</p>
                <p className="text-xs text-amber-800">{scenario.note}</p>
              </div>
            )}
          </div>
        )}

        {/* 분석 단계 */}
        {step === 'analysis' && (
          <div className="space-y-3">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
              <p className="text-xs font-bold text-purple-600 mb-2 flex items-center gap-1">
                <Sparkles size={11} /> AI 진단
              </p>
              <p className="text-xs text-purple-800">{scenario.analysis.diagnosis}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
              <p className="text-xs font-bold text-orange-600 mb-1">🔍 근본 원인</p>
              <p className="text-xs text-orange-800">{scenario.analysis.rootCause}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs font-bold text-blue-600 mb-1">🛠️ 교정 방법</p>
              <p className="text-xs text-blue-800 whitespace-pre-line">{scenario.analysis.correction}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-600 mb-1">📌 예시</p>
              <p className="text-xs text-slate-700 font-mono whitespace-pre-line">{scenario.analysis.example}</p>
            </div>
            <button
              onClick={() => setStep('correct')}
              className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle size={15} /> 정답 및 후속 학습
            </button>
          </div>
        )}

        {/* 정답 + 후속 단계 */}
        {step === 'correct' && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-xs font-bold text-emerald-600 mb-1 flex items-center gap-1">
                <CheckCircle size={11} /> 정답
              </p>
              <p className="text-sm font-bold text-emerald-700 whitespace-pre-line">{scenario.correctAnswer}</p>
            </div>
            <FollowUpCard followUps={scenario.followUps} />
            <div className="flex gap-2 mt-2">
              <button
                onClick={reset}
                className="flex-1 py-2 border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1"
              >
                <RotateCcw size={12} /> 다시 보기
              </button>
              <button
                onClick={() => {
                  const nextIdx = SCENARIO_KEYS.indexOf(selectedId) + 1
                  if (nextIdx < SCENARIO_KEYS.length) handleSelect(SCENARIO_KEYS[nextIdx])
                  else handleSelect(SCENARIO_KEYS[0])
                }}
                className="flex-1 py-2 bg-purple-500 text-white rounded-xl text-xs font-bold hover:bg-purple-600 flex items-center justify-center gap-1"
              >
                <ChevronRight size={12} /> 다음 시나리오
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 진행 단계 표시 */}
      <div className="flex items-center justify-center gap-2">
        {(['question', 'wrong', 'analysis', 'correct'] as const).map((s, i) => (
          <div
            key={s}
            className={`h-1.5 rounded-full transition-all ${
              s === step ? 'w-8 bg-purple-500' : 'w-3 bg-slate-200'
            }`}
          />
        ))}
      </div>

      {/* 개념 요약 카드 */}
      <div className="card border border-slate-200">
        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
          <Target size={14} className="text-purple-500" /> 핵심 오개념 정리
        </h3>
        <div className="space-y-2">
          {[
            { code: 'NM01', text: '"9의 제곱근"과 "√9"를 혼동 — 전자는 ±3, 후자는 3(양수)' },
            { code: 'NM04', text: '√(a²) = a로 잘못 계산 — 올바른 식: √(a²) = |a|' },
            { code: 'NM05', text: '√가 있으면 무조건 무리수 — 완전제곱수이면 유리수' },
            { code: 'NM07', text: '루트가 있으면 작다는 선입견 — 제곱해서 비교해야 함' },
            { code: 'NM10', text: '√a + √b = √(a+b) — 루트 덧셈은 안이 같아야만 가능' },
          ].map(item => (
            <div key={item.code} className="flex gap-2 items-start bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              <span className="text-xs font-black text-red-400 flex-shrink-0 mt-0.5">{item.code}</span>
              <p className="text-xs text-red-800">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 학습 링크 */}
      <div className="card border border-blue-100 bg-blue-50">
        <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1">
          <BookOpen size={12} /> 관련 학습 단계
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 'NQ01', title: '제곱근의 뜻' },
            { id: 'NQ02', title: '제곱근의 성질' },
            { id: 'NQ03', title: '무리수와 실수' },
            { id: 'NQ04', title: '대소 비교' },
            { id: 'NQ05', title: '곱셈·나눗셈' },
            { id: 'NQ06', title: '덧셈·뺄셈' },
          ].map(step => (
            <div key={step.id} className="bg-white rounded-xl px-3 py-2 border border-blue-100">
              <p className="text-xs font-bold text-blue-600">{step.id}</p>
              <p className="text-xs text-slate-600">{step.title}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
